import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, 
  Search, 
  Filter, 
  Calendar,
  Phone,
  Mail,
  User,
  FileText,
  RefreshCw,
  AlertCircle,
  Loader2,
  Eye,
  X,
  Clock,
  Users,
  Download
} from 'lucide-react';
import { useAdminAPI } from '../hooks/useAdminAPI';
import { InsuranceRequest } from '../types/api';

const SegurosSection: React.FC = () => {
  const { getInsuranceRequests, getInsuranceRequest, updateInsuranceStatus } = useAdminAPI();
  
  const [requests, setRequests] = useState<InsuranceRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<InsuranceRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof InsuranceRequest>('fechaContratacion');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedRequest, setSelectedRequest] = useState<InsuranceRequest | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [numeroPoliza, setNumeroPoliza] = useState<string>('');
  const [isSavingPoliza, setIsSavingPoliza] = useState(false);

  const loadInsuranceRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const status = statusFilter !== 'todos' ? statusFilter : undefined;
      const search = searchTerm.trim() || undefined;
      
      const response = await getInsuranceRequests(status, search);
      
      // Handle both array and paginated response
      const requestsData = Array.isArray(response) 
        ? response 
        : (response as any).data || [];
      
      // Log raw response to debug empty fields
      console.log('🛡️ Insurance requests raw response:', JSON.stringify(requestsData, null, 2));
      if (requestsData.length > 0) {
        console.log('🛡️ First request sample:', {
          id: requestsData[0].id,
          rfc: requestsData[0].rfc,
          curp: requestsData[0].curp,
          sexo: requestsData[0].sexo,
          nombreCompleto: requestsData[0].nombreCompleto,
          beneficiario: requestsData[0].beneficiario,
          beneficiarioTelefono: requestsData[0].beneficiarioTelefono,
          beneficiarioEmail: requestsData[0].beneficiarioEmail,
          tipoSeguro: requestsData[0].tipoSeguro,
          plazo: requestsData[0].plazo,
          monto: requestsData[0].monto,
          montoType: typeof requestsData[0].monto,
          montoRaw: requestsData[0].monto,
          allKeys: Object.keys(requestsData[0])
        });
        // Log all montos to see if they're all the same
        const montos = requestsData.map((r: any) => ({ id: r.id, monto: r.monto }));
        console.log('🛡️ All montos in response:', montos);
      }
      
      setRequests(requestsData);
      setFilteredRequests(requestsData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar las solicitudes de seguros';
      setError(errorMessage);
      console.error('Error loading insurance requests:', err);
    } finally {
      setIsLoading(false);
    }
  }, [getInsuranceRequests, statusFilter, searchTerm]);

  useEffect(() => {
    loadInsuranceRequests();
  }, [loadInsuranceRequests]);

  // Filter and search functionality
  useEffect(() => {
    let filtered = requests;

    // Apply search filter (client-side filtering as backup)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(request =>
        (request.nombreCompleto?.toLowerCase().includes(searchLower) ?? false) ||
        // Legacy support for separated name fields
        (request.nombre?.toLowerCase().includes(searchLower) ?? false) ||
        (request.apellidoPaterno?.toLowerCase().includes(searchLower) ?? false) ||
        (request.apellidoMaterno?.toLowerCase().includes(searchLower) ?? false) ||
        request.telefono.includes(searchTerm) ||
        request.email.toLowerCase().includes(searchLower) ||
        (request.rfc?.toLowerCase().includes(searchLower) ?? false) ||
        (request.curp?.toLowerCase().includes(searchLower) ?? false) ||
        (request.beneficiario?.toLowerCase().includes(searchLower) ?? false) ||
        (request.tipoSeguro?.toLowerCase().includes(searchLower) ?? false) ||
        (request.plazo?.toLowerCase().includes(searchLower) ?? false)
      );
    }

    // Apply status filter (client-side filtering as backup)
    if (statusFilter !== 'todos') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    setFilteredRequests(filtered);
  }, [requests, searchTerm, statusFilter]);

  // Sort functionality
  const handleSort = (field: keyof InsuranceRequest) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedRequests = [...filteredRequests].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    // Handle null/undefined values - treat them as empty strings for comparison
    const aStr = aValue == null ? '' : String(aValue);
    const bStr = bValue == null ? '' : String(bValue);
    
    if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
    if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Aprobado':
        return 'bg-green-100 text-green-800';
      case 'Rechazado':
        return 'bg-red-100 text-red-800';
      case 'En Proceso':
        return 'bg-yellow-100 text-yellow-800';
      case 'Pendiente':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const handleRefresh = () => {
    loadInsuranceRequests();
  };

  const handleViewDetails = async (requestId: string) => {
    setIsLoadingDetail(true);
    setIsDetailModalOpen(true);
    try {
      const detail = await getInsuranceRequest(requestId);
      setSelectedRequest(detail);
      setNumeroPoliza(detail.numeroPoliza || '');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar los detalles';
      setError(errorMessage);
      console.error('Error loading insurance request details:', err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleSavePoliza = async () => {
    if (!selectedRequest) return;
    
    const polizaNumber = numeroPoliza.trim();
    if (!polizaNumber) {
      setError('Por favor ingrese un número de póliza');
      return;
    }
    
    setIsSavingPoliza(true);
    setError(null);
    
    try {
      // Actualizar el estado a "Aprobado" y guardar el número de póliza
      const updatedRequest = await updateInsuranceStatus(selectedRequest.id, {
        status: 'Aprobado',
        individualPolicyNumber: polizaNumber
      });
      
      // Actualizar el estado local con la respuesta del backend
      setSelectedRequest(updatedRequest);
      setNumeroPoliza(polizaNumber);
      
      // Recargar la lista para reflejar los cambios
      await loadInsuranceRequests();
      
      console.log('✅ Número de póliza guardado exitosamente:', polizaNumber);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al guardar el número de póliza';
      setError(errorMessage);
      console.error('Error saving policy number:', err);
    } finally {
      setIsSavingPoliza(false);
    }
  };

  const getBeneficiaryName = (request: InsuranceRequest) => {
    // New format: beneficiario is a simple string (can be "N/A" or the name)
    if (request.beneficiario && request.beneficiario !== 'N/A') {
      return request.beneficiario;
    }
    return 'N/A';
  };

  const getContractorName = (request: InsuranceRequest) => {
    // New format: nombreCompleto
    if (request.nombreCompleto) {
      return request.nombreCompleto;
    }
    // Legacy support for separated fields
    if (request.nombre && request.apellidoPaterno) {
      return `${request.nombre} ${request.apellidoPaterno} ${request.apellidoMaterno || ''}`.trim();
    }
    return 'N/A';
  };

  // Función para escapar valores CSV (maneja comillas y saltos de línea)
  const escapeCSVValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }
    const stringValue = String(value);
    // Si contiene comillas, comas o saltos de línea, envolver en comillas y escapar comillas internas
    if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  // Función para descargar la tabla como CSV
  const handleDownloadCSV = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('📥 Iniciando descarga CSV de todas las solicitudes...');
      
      // Obtener TODAS las solicitudes sin límite de paginación
      // Usar los mismos filtros que están aplicados (status y search)
      const status = statusFilter !== 'todos' ? statusFilter : undefined;
      const search = searchTerm.trim() || undefined;
      
      // Hacer una llamada con un límite muy alto para obtener todas las solicitudes
      // O mejor aún, hacer múltiples llamadas si hay paginación
      let allRequests: InsuranceRequest[] = [];
      let currentPage = 1;
      let hasMorePages = true;
      const limitPerPage = 1000; // Límite alto por página
      
      while (hasMorePages) {
        console.log(`📄 Obteniendo página ${currentPage}...`);
        const response = await getInsuranceRequests(status, search, currentPage, limitPerPage);
        
        // Manejar respuesta paginada o array directo
        let pageData: InsuranceRequest[] = [];
        let pagination: { total: number; totalPages: number; page: number } | null = null;
        
        if (Array.isArray(response)) {
          // Respuesta directa como array (sin paginación)
          pageData = response;
          console.log(`📊 Respuesta como array directo: ${pageData.length} registros`);
          allRequests = [...allRequests, ...pageData];
          hasMorePages = false;
        } else {
          // Respuesta paginada con estructura { data: [], pagination: {} }
          pageData = (response as any).data || [];
          pagination = (response as any).pagination;
          
          // Log para verificar estructura de datos
          if (pageData.length > 0) {
            console.log(`📊 Página ${currentPage}:`, {
              registrosEnPagina: pageData.length,
              tienePaginacion: !!pagination,
              totalPages: pagination?.totalPages,
              primerRegistro: {
                id: pageData[0].id,
                rfc: pageData[0].rfc,
                curp: pageData[0].curp,
                sexo: pageData[0].sexo,
                tieneRFC: 'rfc' in pageData[0],
                tieneCURP: 'curp' in pageData[0],
                tieneSexo: 'sexo' in pageData[0]
              }
            });
          }
          
          // Agregar los datos de esta página
          allRequests = [...allRequests, ...pageData];
          
          // Determinar si hay más páginas
          if (pagination) {
            hasMorePages = currentPage < pagination.totalPages;
            currentPage++;
          } else {
            // No hay información de paginación, asumir que es la única página
            hasMorePages = false;
          }
        }
        
        // Si la página está vacía, no hay más datos
        if (pageData.length === 0) {
          hasMorePages = false;
        }
      }
      
      console.log(`✅ Total de solicitudes obtenidas para CSV: ${allRequests.length}`);
      
      // Log de muestra para verificar que los datos tienen RFC, CURP y sexo
      if (allRequests.length > 0) {
        console.log('🔍 Muestra de datos obtenidos para CSV (primer registro):', {
          id: allRequests[0].id,
          rfc: allRequests[0].rfc,
          curp: allRequests[0].curp,
          sexo: allRequests[0].sexo,
          rfcRaw: JSON.stringify(allRequests[0].rfc),
          curpRaw: JSON.stringify(allRequests[0].curp),
          sexoRaw: JSON.stringify(allRequests[0].sexo),
          allKeys: Object.keys(allRequests[0])
        });
      }
      
      if (allRequests.length === 0) {
        setError('No hay solicitudes para descargar');
        setIsLoading(false);
        return;
      }
      
      // Aplicar el mismo ordenamiento que la tabla
      const sortedAllRequests = [...allRequests].sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];
        const aStr = aValue == null ? '' : String(aValue);
        const bStr = bValue == null ? '' : String(bValue);
        if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      // Encabezados del CSV
      const headers = [
        'Fecha Contratación',
        'Teléfono',
        'Email',
        'Nombre Completo',
        'Fecha Nacimiento',
        'RFC',
        'CURP',
        'Sexo',
        'Estado',
        'Beneficiario',
        'Teléfono Beneficiario',
        'Email Beneficiario',
        'Tipo Seguro',
        'Plazo',
        'Número de Póliza'
      ];

      // Crear las filas de datos
      const csvRows = [
        headers.map(escapeCSVValue).join(',')
      ];

      // Agregar cada solicitud como una fila
      sortedAllRequests.forEach((request, index) => {
        // Log para debug (solo primeros 3 registros)
        if (index < 3) {
          console.log(`📋 Registro ${index + 1} para CSV:`, {
            rfc: request.rfc,
            curp: request.curp,
            sexo: request.sexo,
            rfcType: typeof request.rfc,
            curpType: typeof request.curp,
            sexoType: typeof request.sexo
          });
        }
        
        // Extraer valores asegurándonos de que sean strings válidos
        const rfcValue = request.rfc != null ? String(request.rfc).trim() : '';
        const curpValue = request.curp != null ? String(request.curp).trim() : '';
        const sexoValue = request.sexo != null ? String(request.sexo).trim() : '';
        
        // Log adicional para los primeros registros
        if (index < 3) {
          console.log(`📝 Valores procesados para CSV registro ${index + 1}:`, {
            rfcOriginal: request.rfc,
            rfcProcessed: rfcValue,
            curpOriginal: request.curp,
            curpProcessed: curpValue,
            sexoOriginal: request.sexo,
            sexoProcessed: sexoValue
          });
        }
        
        const row = [
          new Date(request.fechaContratacion).toLocaleDateString('es-MX'),
          request.telefono || '',
          request.email || '',
          getContractorName(request),
          new Date(request.fechaNacimiento).toLocaleDateString('es-MX'),
          rfcValue, // RFC procesado
          curpValue, // CURP procesado
          sexoValue, // Sexo procesado
          getStatusLabel(request.status),
          getBeneficiaryName(request),
          request.beneficiarioTelefono ?? '',
          request.beneficiarioEmail ?? '',
          request.tipoSeguro ?? 'N/A',
          request.plazo ?? 'N/A',
          request.numeroPoliza ?? ''
        ];
        csvRows.push(row.map(escapeCSVValue).join(','));
      });

      // Crear el contenido CSV
      const csvContent = csvRows.join('\n');

      // Crear el blob y descargar
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM para Excel
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Nombre del archivo con fecha
      const dateStr = new Date().toISOString().split('T')[0];
      const filterSuffix = statusFilter !== 'todos' ? `-${statusFilter}` : '';
      link.download = `seguros${filterSuffix}-${dateStr}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ CSV descargado exitosamente con ${sortedAllRequests.length} solicitudes`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al generar el archivo CSV';
      setError(errorMessage);
      console.error('Error downloading CSV:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 sm:h-10 sm:w-10 bg-[#3bbcc8]/10 rounded-lg flex items-center justify-center">
            <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-[#3bbcc8]" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-secondary-900">Solicitudes de Seguros</h2>
            <p className="text-xs sm:text-sm text-secondary-500">Consulta y gestiona las solicitudes de compras de seguros</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 bg-red-50 border-red-200">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-sm font-medium text-red-800">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 flex-1">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Buscar por nombre, teléfono, email, RFC, CURP, beneficiario, tipo de seguro, plazo..."
                className="input-field pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    loadInsuranceRequests();
                  }
                }}
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <select
                className="input-field pl-10"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  // Trigger reload when filter changes
                  setTimeout(() => loadInsuranceRequests(), 100);
                }}
              >
                <option value="todos">Todos los estados</option>
                <option value="Pendiente">Pendiente</option>
                <option value="En Proceso">En Proceso</option>
                <option value="Aprobado">Aprobado</option>
                <option value="Rechazado">Rechazado</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-2">
            <button
              onClick={handleDownloadCSV}
              className="btn-secondary flex items-center space-x-2"
              disabled={isLoading || sortedRequests.length === 0}
              title="Descargar tabla como CSV"
            >
              <Download className="h-4 w-4" />
              <span>Descargar CSV</span>
            </button>
            <button
              onClick={handleRefresh}
              className="btn-secondary flex items-center space-x-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span>Actualizar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-medium text-secondary-900">
              {isLoading ? 'Cargando...' : `${filteredRequests.length} solicitudes encontradas`}
            </h3>
            {searchTerm && (
              <p className="text-sm text-secondary-500 mt-1">
                Resultados para: "{searchTerm}"
              </p>
            )}
          </div>
          <div className="text-sm text-secondary-500">
            {statusFilter !== 'todos' && `Filtrado por: ${statusFilter}`}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#3bbcc8]" />
            <span className="ml-3 text-secondary-600">Cargando solicitudes...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-50">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('fechaContratacion')}
                  >
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>Fecha Contratación</span>
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('telefono')}
                  >
                    <div className="flex items-center space-x-1">
                      <Phone className="h-4 w-4" />
                      <span>Teléfono</span>
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center space-x-1">
                      <Mail className="h-4 w-4" />
                      <span>Email</span>
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('nombre')}
                  >
                    <div className="flex items-center space-x-1">
                      <User className="h-4 w-4" />
                      <span>Nombre Completo</span>
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('fechaNacimiento')}
                  >
                    Fecha Nacimiento
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('rfc')}
                  >
                    RFC
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('curp')}
                  >
                    CURP
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('sexo')}
                  >
                    Sexo
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    <div className="flex items-center space-x-1">
                      <Users className="h-4 w-4" />
                      <span>Beneficiario</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Tipo Seguro
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>Plazo</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {sortedRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-secondary-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                      {new Date(request.fechaContratacion).toLocaleDateString('es-MX')}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                      {request.telefono}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                      {request.email}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                      <div>
                        <div className="font-medium">
                          {getContractorName(request)}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                      {new Date(request.fechaNacimiento).toLocaleDateString('es-MX')}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900 font-mono">
                      {request.rfc || '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900 font-mono">
                      {request.curp || '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                      {request.sexo || '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                        {getStatusLabel(request.status)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                      {getBeneficiaryName(request)}
                      {(request.beneficiarioTelefono || request.beneficiarioEmail) && (
                        <div className="text-xs text-secondary-500 mt-1">
                          {request.beneficiarioTelefono && `Tel: ${request.beneficiarioTelefono}`}
                          {request.beneficiarioTelefono && request.beneficiarioEmail && ' • '}
                          {request.beneficiarioEmail && `Email: ${request.beneficiarioEmail}`}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                      {request.tipoSeguro || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                      {request.plazo || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleViewDetails(request.id)}
                        className="btn-secondary flex items-center space-x-1 px-3 py-1 text-xs"
                        title="Ver detalles completos"
                      >
                        <Eye className="h-3 w-3" />
                        <span>Detalles</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* No Results */}
      {!isLoading && filteredRequests.length === 0 && (
        <div className="card p-8 text-center">
          <FileText className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 mb-2">No se encontraron solicitudes</h3>
          <p className="text-secondary-500">
            {searchTerm 
              ? `No hay resultados para "${searchTerm}"`
              : 'No hay solicitudes de seguros registradas'
            }
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {isDetailModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-secondary-900">Detalles de la Contratación</h3>
              <button
                onClick={() => {
                  setIsDetailModalOpen(false);
                  setSelectedRequest(null);
                }}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {isLoadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#3bbcc8]" />
                <span className="ml-3 text-secondary-600">Cargando detalles...</span>
              </div>
            ) : selectedRequest ? (
              <div className="p-6 space-y-6">
                {/* Información del Contratante */}
                <div className="border-b border-secondary-200 pb-4">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center space-x-2">
                    <User className="h-5 w-5 text-[#3bbcc8]" />
                    <span>Información del Contratante</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-secondary-500 uppercase">Nombre Completo</label>
                      <p className="text-sm text-secondary-900 mt-1">
                        {getContractorName(selectedRequest)}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-secondary-500 uppercase">Teléfono</label>
                      <p className="text-sm text-secondary-900 mt-1">{selectedRequest.telefono}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-secondary-500 uppercase">Email</label>
                      <p className="text-sm text-secondary-900 mt-1">{selectedRequest.email}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-secondary-500 uppercase">Fecha de Nacimiento</label>
                      <p className="text-sm text-secondary-900 mt-1">
                        {new Date(selectedRequest.fechaNacimiento).toLocaleDateString('es-MX')}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-secondary-500 uppercase">RFC</label>
                      <p className="text-sm text-secondary-900 mt-1 font-mono">{selectedRequest.rfc || '—'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-secondary-500 uppercase">CURP</label>
                      <p className="text-sm text-secondary-900 mt-1 font-mono">{selectedRequest.curp || '—'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-secondary-500 uppercase">Sexo</label>
                      <p className="text-sm text-secondary-900 mt-1">{selectedRequest.sexo || '—'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-secondary-500 uppercase">Fecha de Contratación</label>
                      <p className="text-sm text-secondary-900 mt-1">
                        {new Date(selectedRequest.fechaContratacion).toLocaleDateString('es-MX')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Información del Beneficiario */}
                {(selectedRequest.beneficiario && selectedRequest.beneficiario !== 'N/A') && (
                  <div className="border-b border-secondary-200 pb-4">
                    <h4 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center space-x-2">
                      <Users className="h-5 w-5 text-[#3bbcc8]" />
                      <span>Información del Beneficiario</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-secondary-500 uppercase">Nombre Completo</label>
                        <p className="text-sm text-secondary-900 mt-1">
                          {getBeneficiaryName(selectedRequest)}
                        </p>
                      </div>
                      {selectedRequest.beneficiarioTelefono && (
                        <div>
                          <label className="text-xs font-medium text-secondary-500 uppercase">Teléfono</label>
                          <p className="text-sm text-secondary-900 mt-1">{selectedRequest.beneficiarioTelefono}</p>
                        </div>
                      )}
                      {selectedRequest.beneficiarioEmail && (
                        <div>
                          <label className="text-xs font-medium text-secondary-500 uppercase">Email</label>
                          <p className="text-sm text-secondary-900 mt-1">{selectedRequest.beneficiarioEmail}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Detalles del Seguro */}
                <div className="border-b border-secondary-200 pb-4">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-[#3bbcc8]" />
                    <span>Detalles del Seguro</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedRequest.tipoSeguro && (
                      <div>
                        <label className="text-xs font-medium text-secondary-500 uppercase">Tipo de Seguro</label>
                        <p className="text-sm text-secondary-900 mt-1">{selectedRequest.tipoSeguro}</p>
                      </div>
                    )}
                    {selectedRequest.plazo && (
                      <div>
                        <label className="text-xs font-medium text-secondary-500 uppercase">Plazo</label>
                        <p className="text-sm text-secondary-900 mt-1">{selectedRequest.plazo}</p>
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <label className="text-xs font-medium text-secondary-500 uppercase mb-2 block">
                        Número de Póliza
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={numeroPoliza}
                          onChange={(e) => setNumeroPoliza(e.target.value)}
                          placeholder="Ingrese el número de póliza"
                          className="input-field flex-1"
                        />
                        <button
                          onClick={handleSavePoliza}
                          disabled={
                            isSavingPoliza || 
                            numeroPoliza.trim() === '' || 
                            numeroPoliza.trim() === (selectedRequest.numeroPoliza || '')
                          }
                          className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          {isSavingPoliza ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Guardando...</span>
                            </>
                          ) : (
                            <span>Guardar</span>
                          )}
                        </button>
                      </div>
                      {selectedRequest.numeroPoliza && (
                        <p className="text-xs text-secondary-500 mt-1">
                          Póliza actual: {selectedRequest.numeroPoliza}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Estado y Notas */}
                <div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-[#3bbcc8]" />
                    <span>Estado y Notas</span>
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-secondary-500 uppercase">Estado</label>
                      <p className="mt-1">
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedRequest.status)}`}>
                          {getStatusLabel(selectedRequest.status)}
                        </span>
                      </p>
                    </div>
                    {selectedRequest.notes && (
                      <div>
                        <label className="text-xs font-medium text-secondary-500 uppercase">Notas</label>
                        <p className="text-sm text-secondary-900 mt-1">{selectedRequest.notes}</p>
                      </div>
                    )}
                    {selectedRequest.rejectedReason && (
                      <div>
                        <label className="text-xs font-medium text-secondary-500 uppercase">Motivo de Rechazo</label>
                        <p className="text-sm text-red-600 mt-1">{selectedRequest.rejectedReason}</p>
                      </div>
                    )}
                    {selectedRequest.approvedBy && (
                      <div>
                        <label className="text-xs font-medium text-secondary-500 uppercase">Aprobado por</label>
                        <p className="text-sm text-secondary-900 mt-1">{selectedRequest.approvedBy}</p>
                      </div>
                    )}
                    {selectedRequest.approvedAt && (
                      <div>
                        <label className="text-xs font-medium text-secondary-500 uppercase">Fecha de Aprobación</label>
                        <p className="text-sm text-secondary-900 mt-1">
                          {new Date(selectedRequest.approvedAt).toLocaleDateString('es-MX', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center">
                <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                <p className="text-secondary-600">No se pudieron cargar los detalles</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SegurosSection;
